import React from "react";
import styled from "styled-components";
import {
  Avatar,
  AvatarSizeVariant,
  CopyInput,
  PaymentMethod,
  Typography,
  TypographyVariant,
} from "../../../../../components/ui-kit";
import {
  PaymentProviders,
  PaymentStatus,
} from "@shared_packages/toolkit/types";
import { DateTime } from "luxon";
import { countries } from "../../../../../constants/countries";
import { CustomerApiType, OrderApiType } from "../../../../../util/types";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 900px) {
    flex-basis: 100%;
  }
`;

const CustomerBlock = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 20px;
  @media (max-width: 900px) {
    padding: 10px;
  }
`;

const PaymentMethodBlock = styled(CustomerBlock)``;
const AddressBlock = styled(CustomerBlock)``;

const TopLineCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
`;

const NameAndLabel = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 10px;
`;

const InfoTypography = styled(Typography)`
  line-height: 20px;
`;

const IdTypography = styled(InfoTypography)`
  overflow-y: auto;
  @media (max-width: 1500px) {
    max-width: 155px;
  }
  @media (max-width: 1320px) {
    max-width: 120px;
  }
  @media (max-width: 1270px) {
    max-width: 100px;
  }
`;

const EmailLink = styled.a`
  color: #027aff;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }
`;

const PaymentWrapper = styled.div`
  margin-top: 10px;
`;

const SpaceBetweenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AwaitingPaymentWrapper = styled.div`
  border-top: 1px solid #eeeeee;
  margin: 20px -20px 0;
`;

const QRWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px 20px 0;
`;

const QRImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 137px;
  height: 137px;
  background: #ffffff;
  border: 1px solid #dbe3eb;
  border-radius: 10px;
  margin: 10px 0;
`;

const QRCode = styled.img`
  width: 100%;
  border-radius: 10px;
`;

const AddressTitle = styled(Typography)`
  margin: 10px 0 7px;
`;

interface InformationBlockProps {
  data: OrderApiType;
  customer?: CustomerApiType;
}

export const InformationBlock: React.FunctionComponent<
  InformationBlockProps
> = ({ data, customer }) => {
  return (
    <Container>
      <CustomerBlock>
        <TopLineCard>
          <Typography variant={TypographyVariant.HEADER2}>Customer </Typography>
        </TopLineCard>
        {customer ? (
          <CustomerInfo>
            <Avatar
              avatarSrc={customer.avatar?.url ?? ""}
              size={AvatarSizeVariant.MEDIUM}
              disableDropdown
            />
            <NameAndLabel>
              <Typography variant={TypographyVariant.HEADER4}>
                {customer.name
                  ? customer.name
                  : customer.firstName && customer.lastName
                  ? `${customer.firstName} ${customer.lastName}`
                  : null}
              </Typography>
              <EmailLink
                href={customer.email ? `mailto:${customer.email}` : undefined}
              >
                {customer.email ?? null}
              </EmailLink>
              <InfoTypography variant={TypographyVariant.BODY3}>
                {customer.phoneCode && customer.phoneNumber
                  ? `${customer.phoneCode} ${customer.phoneNumber}`
                  : null}
              </InfoTypography>
            </NameAndLabel>
          </CustomerInfo>
        ) : (
          <Typography variant={TypographyVariant.HEADER4}>N/A</Typography>
        )}
      </CustomerBlock>

      <PaymentMethodBlock>
        <TopLineCard>
          <Typography variant={TypographyVariant.HEADER2}>
            Payment method
          </Typography>
        </TopLineCard>
        {data.payment &&
        data.payment.paymentProvider === PaymentProviders.FENA_OB ? (
          <>
            <PaymentMethod method={data.payment.paymentProvider} />
            <PaymentWrapper>
              <SpaceBetweenRow>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  Transaction ID:
                </InfoTypography>
                <IdTypography variant={TypographyVariant.BODY3}>
                  {data.payment._id}
                </IdTypography>
              </SpaceBetweenRow>
              <SpaceBetweenRow>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  Date completed:
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.payment.completedAt
                    ? DateTime.fromISO(data.payment.completedAt).toFormat(
                        "dd LLL yyyy"
                      )
                    : "Not fulfilled"}
                </InfoTypography>
              </SpaceBetweenRow>
            </PaymentWrapper>
            {(data.payment?.status === PaymentStatus.SENT ||
              data.payment?.status === PaymentStatus.PENDING) && (
              <AwaitingPaymentWrapper>
                <QRWrapper>
                  <Typography variant={TypographyVariant.HEADER3}>
                    Scan to complete the payment
                  </Typography>
                  <QRImageWrapper>
                    <QRCode src={data?.payment?.qrCodeData} alt="Qr-code" />
                  </QRImageWrapper>
                  <Typography
                    variant={TypographyVariant.HEADER3}
                    style={{ marginBottom: "7px" }}
                  >
                    Or follow the link
                  </Typography>
                  <CopyInput link={data.payment?.link} />
                </QRWrapper>
              </AwaitingPaymentWrapper>
            )}
          </>
        ) : data.payment &&
          (data.payment.paymentProvider === PaymentProviders.CASH ||
            data.payment.paymentProvider === PaymentProviders.OTHER) ? (
          <>
            <PaymentMethod method={data.payment.paymentProvider} />
            <PaymentWrapper>
              <SpaceBetweenRow>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  Date completed:
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.payment.completedAt
                    ? DateTime.fromISO(data.payment.completedAt).toFormat(
                        "dd LLL yyyy"
                      )
                    : "Not fulfilled"}
                </InfoTypography>
              </SpaceBetweenRow>
            </PaymentWrapper>
          </>
        ) : (
          <Typography variant={TypographyVariant.HEADER4}>N/A</Typography>
        )}
      </PaymentMethodBlock>

      <AddressBlock>
        <TopLineCard style={{ marginBottom: "0px" }}>
          <Typography variant={TypographyVariant.HEADER2}>Address</Typography>
        </TopLineCard>

        {!data.shippingAddress && !data.billingAddress && !data.pickupPoint ? (
          <Typography variant={TypographyVariant.HEADER4}>N/A</Typography>
        ) : (
          <>
            {data.shippingAddress && (
              <>
                <AddressTitle variant={TypographyVariant.HEADER4}>
                  Delivery address
                </AddressTitle>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.contactName
                    ? data.shippingAddress.contactName
                    : null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.phoneCode &&
                  data.shippingAddress?.phoneNumber
                    ? `${data.shippingAddress.phoneCode} ${data.shippingAddress.phoneNumber}`
                    : null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.addressLine1 ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.addressLine2 ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.city ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.shippingAddress?.zipCode ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {countries.find(
                    (c) => c.country === data.shippingAddress?.country
                  )?.countryName ?? null}
                </InfoTypography>
              </>
            )}
            {data.pickupPoint && (
              <>
                <AddressTitle variant={TypographyVariant.HEADER4}>
                  {data.pickupPoint ? "Pickup point" : null}
                </AddressTitle>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.pickupPoint ? data.pickupPoint.name : null}
                </InfoTypography>
              </>
            )}
            {data.billingAddress && (
              <>
                <AddressTitle
                  variant={TypographyVariant.HEADER4}
                  style={{ marginTop: "16px" }}
                >
                  Billing Address
                </AddressTitle>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress?.contactName
                    ? data.billingAddress.contactName
                    : null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress?.phoneCode &&
                  data.billingAddress?.phoneNumber
                    ? `${data.billingAddress.phoneCode} ${data.billingAddress.phoneNumber}`
                    : null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress.addressLine1 ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress.addressLine2 ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress?.city ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress.zipCode ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {data.billingAddress.region ?? null}
                </InfoTypography>
                <InfoTypography variant={TypographyVariant.BODY3}>
                  {countries.find(
                    (c) => c.country === data.billingAddress.country
                  )?.countryName ?? null}
                </InfoTypography>
              </>
            )}
          </>
        )}
      </AddressBlock>
    </Container>
  );
};
